import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";

const ChatMessage = ({ message, isUser, timestamp, animate = false }) => {
  const [displayedText, setDisplayedText] = useState(animate && !isUser ? "" : message);
  const [isAnimating, setIsAnimating] = useState(animate && !isUser);

  useEffect(() => {
    if (!animate || isUser) {
      setDisplayedText(message);
      return;
    }

    setDisplayedText("");
    setIsAnimating(true);

    let currentIndex = 0;
    const textLength = message.length;

    // Speed: ~30ms per character, faster for longer messages
    const baseSpeed = Math.max(10, Math.min(30, 1500 / textLength));

    const intervalId = setInterval(() => {
      if (currentIndex < textLength) {
        // Add multiple characters at once for longer messages
        const charsToAdd = textLength > 200 ? 3 : textLength > 100 ? 2 : 1;
        currentIndex = Math.min(currentIndex + charsToAdd, textLength);
        setDisplayedText(message.slice(0, currentIndex));
      } else {
        clearInterval(intervalId);
        setIsAnimating(false);
      }
    }, baseSpeed);

    return () => clearInterval(intervalId);
  }, [message, animate, isUser]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: "80%",
          display: "flex",
          flexDirection: "column",
          alignItems: isUser ? "flex-end" : "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            flexDirection: isUser ? "row-reverse" : "row",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: isUser ? "primary.main" : "secondary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isUser ? (
              <PersonIcon sx={{ color: "white", fontSize: 18 }} />
            ) : (
              <SmartToyIcon sx={{ color: "white", fontSize: 18 }} />
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: isUser ? "primary.main" : "#f5f5f5",
              color: isUser ? "white" : "text.primary",
              borderRadius: 2,
              px: 2,
              py: 1,
              wordBreak: "break-word",
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {displayedText}
              {isAnimating && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: "2px",
                    height: "1em",
                    backgroundColor: "text.primary",
                    ml: 0.5,
                    animation: "blink 1s step-end infinite",
                    "@keyframes blink": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0 },
                    },
                  }}
                />
              )}
            </Typography>
          </Box>
        </Box>
        {timestamp && !isAnimating && (
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              ml: isUser ? 0 : 5,
              mr: isUser ? 5 : 0,
              color: "text.secondary",
            }}
          >
            {new Date(timestamp).toLocaleTimeString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
